/**
 * Shared scene physics builder.
 *
 * Both the client (`GameScene`) and the server (`HiLoPhysicsWorld`) call this
 * to populate a Cannon-ES world from an `SDLSceneDocument`. Having one
 * implementation is a requirement: during HI_LO the server is authoritative
 * about dice state, and if the two worlds diverge (different collider sizes,
 * positions, or rotations) the reconciliation snapshot will visibly teleport
 * existing dice whenever a new die enters the simulation.
 *
 * Semantics match the client's historical behavior in `GameScene.ts`:
 * - Primitives: shape dims multiplied by object.scale. Physics offset / rotation
 *   on the body are NOT applied (they weren't on the client).
 * - Meshes: shape dims are world-space (NOT multiplied by scale). Physics
 *   offset / rotation ARE applied on top of object.position / object.rotation.
 * - Colliders: same as meshes (world-space dims, offset/rotation applied).
 *
 * Only cannon-es is used — we intentionally avoid a three.js dep so the module
 * runs inside Node (game-server) without extra transitive deps. Euler→Quat
 * uses Cannon's built-in `setFromEuler(x, y, z, 'XYZ')`, which matches THREE's
 * default Euler order.
 */
import * as CANNON from 'cannon-es';
import { SDL_COLLISION_GROUPS, SDL_COLLISION_MASKS } from '../constants/physics.js';
function resolveBodyType(type) {
    switch (type) {
        case 'dynamic': return CANNON.Body.DYNAMIC;
        case 'kinematic': return CANNON.Body.KINEMATIC;
        case 'static':
        default: return CANNON.Body.STATIC;
    }
}
function resolveMaterial(physics, mats) {
    if (physics.collisionGroup === 'TABLE')
        return mats.tableMaterial;
    if (physics.collisionGroup === 'WALL')
        return mats.wallMaterial;
    return undefined;
}
function eulerToQuat(x, y, z) {
    const q = new CANNON.Quaternion();
    q.setFromEuler(x, y, z, 'XYZ');
    return q;
}
function buildShapeForPrimitive(obj) {
    const scale = obj.scale;
    const shape = obj.primitive.shape;
    if (shape === 'plane') {
        return new CANNON.Plane();
    }
    if (shape === 'box') {
        const { width, height, depth } = obj.primitive.parameters;
        return new CANNON.Box(new CANNON.Vec3((width * scale[0]) / 2, (height * scale[1]) / 2, (depth * scale[2]) / 2));
    }
    if (shape === 'sphere') {
        const { radius } = obj.primitive.parameters;
        return new CANNON.Sphere(radius * Math.max(scale[0], scale[1], scale[2]));
    }
    if (shape === 'cylinder') {
        const { radiusTop, radiusBottom, height } = obj.primitive.parameters;
        // NOTE: matches client — does NOT multiply by scale. This is arguably
        // incorrect (cylinders should scale like spheres/boxes), but changing it
        // would break scenes the client has historically rendered correctly.
        return new CANNON.Cylinder(radiusTop, radiusBottom, height, 16);
    }
    return null;
}
function buildShapeForMesh(physics) {
    const shape = physics.shape;
    const size = physics.size;
    if (shape === 'plane') {
        return new CANNON.Plane();
    }
    if (shape === 'box') {
        if (size?.width === undefined || size?.height === undefined || size?.depth === undefined)
            return null;
        return new CANNON.Box(new CANNON.Vec3(size.width / 2, size.height / 2, size.depth / 2));
    }
    if (shape === 'sphere') {
        if (size?.radius === undefined)
            return null;
        return new CANNON.Sphere(size.radius);
    }
    if (shape === 'cylinder') {
        if (size?.radiusTop === undefined ||
            size?.radiusBottom === undefined ||
            size?.cylinderHeight === undefined)
            return null;
        return new CANNON.Cylinder(size.radiusTop, size.radiusBottom, size.cylinderHeight, size.cylinderSegments ?? 16);
    }
    return null;
}
function buildShapeForCollider(obj) {
    const size = obj.size ?? {};
    switch (obj.colliderShape) {
        case 'plane':
            return new CANNON.Plane();
        case 'box':
            return new CANNON.Box(new CANNON.Vec3((size.width ?? 1) / 2, (size.height ?? 1) / 2, (size.depth ?? 1) / 2));
        case 'sphere':
            return new CANNON.Sphere(size.radius ?? 0.5);
        case 'cylinder':
            return new CANNON.Cylinder(size.radiusTop ?? size.radius ?? 0.5, size.radiusBottom ?? size.radius ?? 0.5, size.cylinderHeight ?? size.height ?? 1, size.cylinderSegments ?? 16);
        default:
            return null;
    }
}
function makeBody(shape, physics, position, rotation, mats) {
    const collisionFilterGroup = SDL_COLLISION_GROUPS[physics.collisionGroup] ?? SDL_COLLISION_GROUPS.DEFAULT;
    const collisionFilterMask = SDL_COLLISION_MASKS[physics.collisionGroup] ?? SDL_COLLISION_MASKS.DEFAULT;
    const body = new CANNON.Body({
        type: resolveBodyType(physics.type),
        shape,
        material: resolveMaterial(physics, mats),
        mass: physics.mass ?? 0,
        collisionFilterGroup,
        collisionFilterMask,
    });
    body.position.set(position[0], position[1], position[2]);
    const q = eulerToQuat(rotation[0], rotation[1], rotation[2]);
    body.quaternion.set(q.x, q.y, q.z, q.w);
    return body;
}
/**
 * Populate `world` with physics bodies derived from `document`. Skips dynamic
 * objects (those are added at runtime — e.g. dice, bills). Returns the bodies
 * added so callers can remove them later.
 */
export function buildSceneBodies(world, document, mats) {
    const added = [];
    for (const obj of document.objects) {
        if (obj.type === 'primitive') {
            const physics = obj.physics;
            if (!physics || !physics.enabled)
                continue;
            if (physics.type === 'dynamic')
                continue;
            const shape = buildShapeForPrimitive(obj);
            if (!shape)
                continue;
            // Primitives: position/rotation come from obj only (client never applied
            // physics.offset / physics.rotation on primitives).
            const body = makeBody(shape, physics, obj.position, obj.rotation, mats);
            world.addBody(body);
            added.push(body);
        }
        else if (obj.type === 'mesh') {
            const physics = obj.physics;
            if (!physics || !physics.enabled)
                continue;
            if (physics.type === 'dynamic')
                continue;
            if (physics.shape === 'trimesh' || physics.shape === 'convex')
                continue;
            const shape = buildShapeForMesh(physics);
            if (!shape)
                continue;
            const offset = physics.offset ?? [0, 0, 0];
            const physRot = physics.rotation ?? [0, 0, 0];
            const position = [
                obj.position[0] + offset[0],
                obj.position[1] + offset[1],
                obj.position[2] + offset[2],
            ];
            const rotation = [
                obj.rotation[0] + physRot[0],
                obj.rotation[1] + physRot[1],
                obj.rotation[2] + physRot[2],
            ];
            const body = makeBody(shape, physics, position, rotation, mats);
            world.addBody(body);
            added.push(body);
        }
        else if (obj.type === 'collider') {
            const physics = obj.physics;
            if (!physics || !physics.enabled)
                continue;
            if (physics.type === 'dynamic')
                continue;
            const shape = buildShapeForCollider(obj);
            if (!shape)
                continue;
            const offset = physics.offset ?? [0, 0, 0];
            const physRot = physics.rotation ?? [0, 0, 0];
            const position = [
                obj.position[0] + offset[0],
                obj.position[1] + offset[1],
                obj.position[2] + offset[2],
            ];
            const rotation = [
                obj.rotation[0] + physRot[0],
                obj.rotation[1] + physRot[1],
                obj.rotation[2] + physRot[2],
            ];
            const body = makeBody(shape, physics, position, rotation, mats);
            world.addBody(body);
            added.push(body);
        }
    }
    return added;
}
//# sourceMappingURL=ScenePhysicsBuilder.js.map